import * as R from 'ramda';
import memoize from 'memoize-one';
import {
  i18n,
  locales,
  getDatasource,
  defaultViewer,
  sortOptionIndex,
} from '../lib/settings';
import { fromSearchToState } from '../utils/router';
import { OVERVIEW, TABLE } from '../utils/constants';
import { cleanifyLayoutIds, getBasename } from '../utils';

const getRouter = R.prop('router');

const getLocation = memoize((state) =>
  R.propOr({}, 'location', getRouter(state)),
);

// This is the most important one to memoize to stop the "new object" waterfall
export const getLocationState = memoize((state) => {
  const routerState = R.prop('state', getLocation(state));
  return routerState ?? fromSearchToState(window.location.search);
});

export const getPathname = memoize((state) => {
  const path = R.propOr('/', 'pathname', getLocation(state));
  const cleanPath = R.replace(getBasename(), '', path);
  const normalizedPath = cleanPath.startsWith('/')
    ? cleanPath
    : `/${cleanPath}`;
  return normalizedPath;
});

export const getSortIndexSelected = memoize((state) =>
  R.propOr(sortOptionIndex, 'sortIndexSelected', getLocationState(state)),
);

export const getTerm = memoize((state) =>
  R.propOr('', 'term', getLocationState(state)),
);

export const getSearchResultNb = memoize((state) =>
  R.propOr(0, 'searchResultNb', getLocationState(state)),
);

export const getIsBypass = memoize((state) =>
  R.prop('bypass', getLocationState(state)),
);

export const getStart = memoize((state) =>
  R.prop('start', getLocationState(state)),
);

export const getLocale = memoize((state) =>
  R.propOr(i18n.localeId, 'locale', getLocationState(state)),
);

export const getFacet = memoize((state) =>
  R.prop('facet', getLocationState(state)),
);

export const getConstraints = memoize((state) =>
  R.propOr({}, 'constraints', getLocationState(state)),
);

export const getDataflow = memoize((state) =>
  R.propOr({}, 'dataflow', getLocationState(state)),
);

export const getDataquery = memoize((state) =>
  R.propOr('', 'dataquery', getLocationState(state)),
);

export const getHasAccessibility = memoize((state) =>
  R.prop('hasAccessibility', getLocationState(state)),
);

export const getHasDataAvailability = memoize((state) =>
  R.propOr(true, 'hasDataAvailability', getLocationState(state)),
);

export const getIsDataAvaibilityInState = memoize(
  (state) => !R.isNil(R.prop('hasDataAvailability', getLocationState(state))),
);

export const getViewer = memoize((state) =>
  R.propOr(defaultViewer, 'viewer', getLocationState(state)),
);

export const getMap = memoize((state) =>
  R.prop('map', getLocationState(state)),
);

export const getPeriod = memoize((state) =>
  R.prop('period', getLocationState(state)),
);

const _getTableLayout = memoize((state) =>
  R.prop('layout', getLocationState(state)),
);

export const getDisplay = memoize((state) =>
  R.propOr('label', 'display', getLocationState(state)),
);

export const getTimeDimensionOrders = memoize((state) =>
  R.propOr({}, 'time', getLocationState(state)),
);

export const getMicrodataConstraints = memoize((state) =>
  R.propOr({}, 'microdataConstraints', getLocationState(state)),
);

export const getIsOverview = memoize((state) =>
  R.equals(OVERVIEW, getViewer(state)),
);

export const getIsTable = memoize((state) => R.equals(TABLE, getViewer(state)));

export const getHasLastNObservations = memoize((state) => {
  const datasourceId = R.prop('datasourceId', getDataflow(state));
  return R.propOr(false, 'hasLastNObservations', getDatasource(datasourceId));
});

export const getIsShareable = memoize((state) => {
  const datasourceId = R.prop('datasourceId', getDataflow(state));
  return R.propOr(true, 'isShareable', getDatasource(datasourceId));
});

export const getTableLayout = memoize((state) =>
  cleanifyLayoutIds(_getTableLayout(state)),
);

export const getLastNObservations = memoize((state) =>
  R.prop('lastNObservations', getLocationState(state)),
);

export const getLastNMode = memoize((state) =>
  R.prop('lastNMode', getLocationState(state)),
);

export const getIsRtl = memoize((state) => {
  const localeId = getLocale(state);
  return R.path([localeId, 'isRtl'], locales);
});
